import { asArray, legacySum, sumOf } from './promoPrimitives';
import { getProduct } from './promoModel';

// Units of a promo negotiation (#23521, spec RG-02 / RG-10) : the volume is keyed in UB (the product's own container :
// carton, pallet...) and converted to UC through the product packing.

/** `getVolumeUC` (visitStep3-promo.js:954) : volume × packing of the container, 0 when the product has no such container. */
export function toUC(snapshot, productId, volume, containerId) {
	const qty = Number(getProduct(snapshot, productId)?.packing?.[containerId]) || 0;
	return volume * qty;
}

/**
 * TFS#8185 / TFS#20279 : a product with a single possible container gets it automatically, and loses it at volume 0.
 * The legacy read the `<select>` option count (JS:2683-2695, 2949-2961), here the offer containers are the source.
 */
export function autoContainer(containers, volume, containerId) {
	const list = asArray(containers);
	if (list.length !== 1) return containerId;
	return volume === 0 ? 0 : list[0].id;
}

const inOffer = (row, ref) => row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId;

/** Paying total of an offer : UC (sum of volumeUC), or UB in `displayUnit === 1` mode (sum of the volumes > 0). */
export function totalPaying(rows, ref, ubMode = false) {
	const paying = asArray(rows).filter((row) => inOffer(row, ref) && !row.additional && !row.isFree);
	if (!ubMode) return sumOf(paying, 'volumeUC');
	const positive = paying.filter((row) => row.volume > 0);
	return positive.length ? sumOf(positive, 'volume') : 0;
}

/** Free total of an offer, same units as `totalPaying`. */
export function totalFree(rows, ref, ubMode = false) {
	const free = asArray(rows).filter((row) => inOffer(row, ref) && !row.additional && row.isFree);
	if (!ubMode) return sumOf(free, 'volumeUC');
	const positive = free.filter((row) => row.volume > 0);
	return positive.length ? sumOf(positive, 'volume') : 0;
}

/**
 * `getOfferTotalUBForDisplay` (JS:4946) : `{ total, containerId }` when every negotiated row (paying and free) shares one
 * container, `null` for mixed units (RG-10), `{ total: 0, containerId: 0 }` when nothing is negotiated yet.
 */
export function offerTotalUB(rows, ref) {
	const items = asArray(rows).filter((row) => inOffer(row, ref) && !row.additional && row.volume > 0);
	if (!items.length) return { total: 0, containerId: 0 };
	const containers = [...new Set(items.map((row) => row.productContainerId))];
	if (containers.length > 1) return null;
	return { total: legacySum(items.map((row) => row.volume)), containerId: containers[0] };
}

/** Paying volume of one product, in the offer unit (`isValid` / `canAddFreeProduct` per-product sums). */
export function productPaying(rows, ref, productId, ubMode = false) {
	const items = asArray(rows).filter((row) => inOffer(row, ref) && row.productId === productId && !row.additional && !row.isFree);
	return sumOf(items, ubMode ? 'volume' : 'volumeUC');
}

/** Free volume of one product, in the offer unit. */
export function productFree(rows, ref, productId, ubMode = false) {
	const items = asArray(rows).filter((row) => inOffer(row, ref) && row.productId === productId && !row.additional && row.isFree);
	return sumOf(items, ubMode ? 'volume' : 'volumeUC');
}
